import { useState } from 'react'
import { api, type ApiError } from '../../lib/api'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { StatusBox } from '../ui/badge'
import { FloatingInput } from '../ui/input'

export function DangerZoneSection({
  accessToken,
  tenantSlug,
  tenantName,
  onDeleted,
}: {
  accessToken: string | null
  tenantSlug: string
  tenantName: string
  onDeleted: () => void
}) {
  const [showConfirm, setShowConfirm] = useState(false)
  const [typedSlug, setTypedSlug] = useState('')
  const [draining, setDraining] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function handleDelete() {
    if (!accessToken) return
    setDraining(true)
    setErr(null)
    try {
      await api.deleteWorkspace(accessToken, typedSlug)
      onDeleted()
    } catch (e) {
      const ae = e as ApiError
      setErr(ae.detail ?? ae.title ?? 'Workspace deletion failed')
      setDraining(false)
    }
  }

  return (
    <Card className="ring-destructive/40">
      <CardHeader>
        <CardTitle className="text-destructive">Danger zone</CardTitle>
        <CardDescription>
          Deleting this workspace will <strong className="text-foreground">terminate every Hetzner server</strong> we
          provisioned for you (the only thing that costs you money), archive each
          project's GitLab repo, tear down all live previews, and disconnect your
          Hetzner + GitLab credentials. The action is irreversible.
        </CardDescription>
      </CardHeader>
      {!showConfirm ? (
        <CardContent>
          <Button variant="destructive" size="sm" onClick={() => setShowConfirm(true)}>
            Delete workspace…
          </Button>
        </CardContent>
      ) : (
        <>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Type <span className="font-mono text-foreground">{tenantSlug}</span> to confirm
              deletion of the <strong className="text-foreground">{tenantName}</strong> workspace.
            </p>
            <FloatingInput
              label={`Type ${tenantSlug} to confirm`}
              type="text"
              autoFocus
              value={typedSlug}
              onChange={(e) => setTypedSlug(e.target.value)}
              className="font-mono"
              autoComplete="off"
            />
            {err && <StatusBox tone="destructive">{err}</StatusBox>}
          </CardContent>
          <CardFooter className="justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowConfirm(false)
                setTypedSlug('')
                setErr(null)
              }}
              disabled={draining}
              className="bg-card"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDelete}
              disabled={draining || typedSlug !== tenantSlug}
            >
              {draining ? 'Draining workspace…' : 'I understand — delete it'}
            </Button>
          </CardFooter>
        </>
      )}
    </Card>
  )
}
